import {
    friendlyName,
    isDateFilterRule,
    isWithValueFilter,
    type CompiledMetric,
    type Dimension,
} from '@lightdash/common';
import {
    Anchor,
    Badge,
    Box,
    Button,
    Code,
    Divider,
    Group,
    Popover,
    Stack,
    Text,
    Title,
    Tooltip,
    useMantineTheme,
} from '@mantine-8/core';
import { IconCode } from '@tabler/icons-react';
import ReactMarkdownPreview from '@uiw/react-markdown-preview';
import { Fragment, useState, type FC, type PropsWithChildren } from 'react';
import rehypeExternalLinks from 'rehype-external-links';
import {
    explorerActions,
    useExplorerDispatch,
} from '../../../../features/explorer/store';
import { rehypeRemoveHeaderLinks } from '../../../../utils/markdownUtils';
import FieldIcon from '../../../common/Filters/FieldIcon';
import { filterOperatorLabel } from '../../../common/Filters/FilterInputs/constants';
import MantineIcon from '../../../common/MantineIcon';
import classes from './ItemDetailPreview.module.css';

/**
 * Renders markdown for an item's description, with additional constraints
 * to avoid markdown styling from completely throwing its surroundings out
 * of whack.
 */
export const ItemDetailMarkdown: FC<{ source: string }> = ({ source }) => {
    const theme = useMantineTheme();
    return (
        <ReactMarkdownPreview
            skipHtml
            components={{
                h1: ({ children }) => <Title order={2}>{children}</Title>,
                h2: ({ children }) => <Title order={3}>{children}</Title>,
                h3: ({ children }) => <Title order={4}>{children}</Title>,
                table: ({ children }) => (
                    <table className={classes.markdownTable}>{children}</table>
                ),
                th: ({ children }) => (
                    <th className={classes.markdownCell}>{children}</th>
                ),
                td: ({ children }) => (
                    <td className={classes.markdownCell}>{children}</td>
                ),
            }}
            rehypePlugins={[[rehypeExternalLinks, { target: '_blank' }]]}
            rehypeRewrite={rehypeRemoveHeaderLinks}
            source={source}
            disallowedElements={['img']}
            style={{
                fontSize: theme.fontSizes.sm,
                color: theme.colors.ldGray[7],
                backgroundColor: 'transparent',
            }}
        />
    );
};

/**
 * Renders a truncated version of an item's description, with an option
 * to read the full description if necessary.
 */
export const ItemDetailPreview: FC<{
    description?: string;
    onViewDescription: () => void;
    metricInfo?: {
        type: string;
        sql: string;
        compiledSql: string;
        name: string;
        filters?: CompiledMetric['filters'];
        baseDimension?: Dimension;
    };
}> = ({ description, onViewDescription, metricInfo }) => {
    /**
     * This value is pretty arbitrary - it's an amount of characters that will exceed
     * a single line, and for which the 'Read more' option should make sense, and not
     * be an annoyance.
     *
     * It's better to err on the side of caution, and show the 'Read more' option even
     * if unnecessarily so.
     */
    const isTruncated =
        (description && description.length > 180) ||
        (description && description.split('\n').length > 2);

    const [showCompiled, setShowCompiled] = useState(false);

    return (
        <Stack gap="xs">
            {metricInfo && (
                <>
                    <Group gap="xs" justify="space-between">
                        <Text fz="sm" fw={500} c="ldDark.7">
                            {metricInfo.name}
                        </Text>
                        <Badge
                            radius="sm"
                            color="indigo"
                            p={2}
                            style={{
                                boxShadow: 'var(--mantine-shadow-subtle)',
                                border: '1px solid var(--mantine-color-indigo-1)',
                            }}
                        >
                            {friendlyName(metricInfo.type)}
                        </Badge>
                    </Group>
                    {metricInfo.baseDimension && (
                        <>
                            <Divider color="ldGray.2" />
                            <Stack gap={4}>
                                <Text fz="xs" fw={500} c="ldDark.7">
                                    Source field
                                </Text>
                                <Group gap="xs" wrap="nowrap">
                                    <FieldIcon
                                        item={metricInfo.baseDimension}
                                        size="sm"
                                    />
                                    <Text fz="xs" c="ldGray.7">
                                        {metricInfo.baseDimension.tableLabel}
                                    </Text>
                                    <Text fz="xs" fw={500} c="ldDark.7">
                                        {metricInfo.baseDimension.label ||
                                            metricInfo.baseDimension.name}
                                    </Text>
                                </Group>
                            </Stack>
                        </>
                    )}
                </>
            )}
            {description && (
                <Box
                    mah={120}
                    style={{
                        overflow: 'hidden',
                        // If we're over the truncation limit, use a mask to fade out the bottom of the container.
                        maskImage: isTruncated
                            ? 'linear-gradient(180deg, white 0%, white 80%, transparent 100%)'
                            : undefined,
                    }}
                >
                    <Stack gap="xs">
                        {metricInfo && (
                            <>
                                <Divider color="ldGray.2" />
                                <Text fz="xs" fw={500} c="ldDark.7">
                                    Description
                                </Text>
                            </>
                        )}

                        <ItemDetailMarkdown source={description} />
                    </Stack>
                </Box>
            )}
            {isTruncated && (
                <Box ta={'center'}>
                    <Anchor
                        size={'xs'}
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                            e.preventDefault();
                            onViewDescription();
                        }}
                    >
                        Read full description
                    </Anchor>
                </Box>
            )}
            {metricInfo && (
                <>
                    <Divider color="ldGray.2" />
                    <Stack gap="xs">
                        <Group gap="xs" align="center" justify="space-between">
                            <Text fz="xs" fw={500} c="ldDark.7">
                                SQL
                            </Text>
                            <Tooltip
                                variant="xs"
                                position="right"
                                label={
                                    showCompiled
                                        ? 'Show original SQL'
                                        : 'Show compiled SQL'
                                }
                            >
                                <Button
                                    variant="subtle"
                                    color="gray"
                                    onClick={(
                                        e: React.MouseEvent<HTMLButtonElement>,
                                    ) => {
                                        e.stopPropagation();
                                        setShowCompiled(!showCompiled);
                                    }}
                                    size="compact-xs"
                                    leftSection={
                                        <MantineIcon icon={IconCode} />
                                    }
                                >
                                    {showCompiled
                                        ? 'Original SQL'
                                        : 'Compiled SQL'}
                                </Button>
                            </Tooltip>
                        </Group>
                        <Code maw={400}>
                            {showCompiled
                                ? metricInfo.compiledSql
                                : metricInfo.sql}
                        </Code>
                        {metricInfo.filters &&
                            metricInfo.filters.length > 0 && (
                                <>
                                    <Divider color="ldGray.2" />
                                    <Text fz="xs" fw={500} c="ldDark.7">
                                        Filters
                                    </Text>
                                    {metricInfo.filters.map((filter) => {
                                        const operationLabel =
                                            filterOperatorLabel[
                                                filter.operator
                                            ];

                                        return (
                                            <Group key={filter.id} gap={4}>
                                                <Code fz="xs" fw={500}>
                                                    {filter.target.fieldRef}
                                                </Code>
                                                <Text fz="xs" fw={500}>
                                                    {operationLabel}
                                                </Text>
                                                {}
                                                <Code fz="xs">
                                                    {isWithValueFilter(
                                                        filter.operator,
                                                    )
                                                        ? filter.values
                                                              ?.map(
                                                                  (value) =>
                                                                      value,
                                                              )
                                                              .join(', ')
                                                        : ''}
                                                    {isDateFilterRule(
                                                        filter,
                                                    ) && (
                                                        <Fragment>
                                                            {' '}
                                                            {filter.settings
                                                                ?.completed
                                                                ? 'completed'
                                                                : ''}
                                                            {
                                                                filter.settings
                                                                    ?.unitOfTime
                                                            }
                                                        </Fragment>
                                                    )}
                                                </Code>
                                            </Group>
                                        );
                                    })}
                                </>
                            )}
                    </Stack>
                </>
            )}
        </Stack>
    );
};

/**
 * Helper for using ItemDetailPreview with tables or models.
 */
export const TableItemDetailPreview = ({
    showPreview,
    closePreview,
    label,
    description,
    /** Position offset for the preview popover */
    offset = 20,
    tableMetadata,
    children,
}: PropsWithChildren<{
    showPreview: boolean;
    closePreview: () => void;
    description?: string;
    label: string;
    offset?: number;
    tableMetadata?: {
        name: string;
        dbtPackageName?: string;
        ymlPath?: string;
        sqlPath?: string;
    };
}>) => {
    const dispatch = useExplorerDispatch();

    const onOpenDescriptionView = () => {
        closePreview();
        dispatch(
            explorerActions.openItemDetail({
                itemType: 'table',
                label,
                description,
                tableMetadata,
            }),
        );
    };

    return (
        <Popover
            opened={showPreview}
            keepMounted={false}
            shadow="sm"
            withinPortal
            disabled={!description}
            position="right"
            withArrow
            offset={offset}
        >
            <Popover.Target>{children}</Popover.Target>
            <Popover.Dropdown
                /**
                 * Takes up space to the right, so it's OK to go fairly wide in the interest
                 * of readability.
                 */
                maw={500}
                /**
                 * If we don't stop propagation, users may unintentionally toggle dimensions/metrics
                 * while interacting with the hovercard.
                 */
                onClick={(event) => event.stopPropagation()}
            >
                <ItemDetailPreview
                    onViewDescription={onOpenDescriptionView}
                    description={description}
                />
            </Popover.Dropdown>
        </Popover>
    );
};
